import React from "react";

/**
 * =====================================================================
 * LogoutModal
 * =====================================================================
 *
 * RÔLE
 * ---------------------------------------------------------------------
 * Cette modal apparaît lorsqu’un utilisateur tente de se déconnecter
 * alors que son panier contient encore des articles.
 *
 * OBJECTIF
 * ---------------------------------------------------------------------
 * Éviter une déconnexion trop brutale en rappelant à l’utilisateur que :
 * - son panier actuel est en cours
 * - ses avantages membre seront perdus
 * - sa sélection sera réinitialisée s’il continue comme visiteur
 *
 * PRINCIPES DE CETTE VERSION
 * ---------------------------------------------------------------------
 * 1. Le composant utilise désormais les clés de traduction via `t`
 * 2. Le panier est sécurisé avec une valeur par défaut `[]`
 * 3. Les boutons reprennent un style proche du HeroSection :
 *    - plus premium
 *    - plus élégant
 *    - plus cohérent avec l’univers graphique
 * 4. Le composant reste autonome :
 *    - si `show` est false -> rien n’est rendu
 *    - si `show` est true  -> la modal apparaît
 *
 * PROPS ATTENDUES
 * ---------------------------------------------------------------------
 * show                : boolean -> affiche ou masque la modal
 * setShow             : function -> permet de fermer la modal
 * panier              : array -> contenu actuel du panier
 * handleConfirmLogout : function -> action confirmée de déconnexion
 * t                   : object -> dictionnaire de traductions
 */
export default function LogoutModal({
  show,
  setShow,
  panier = [],
  handleConfirmLogout,
  t = {},
}) {
  /**
   * ===================================================================
   * GARDE DE RENDU
   * ===================================================================
   *
   * Si show est false, la modal n’est pas montée.
   * Cela évite tout rendu inutile dans le DOM.
   */
  if (!show) return null;

  /**
   * ===================================================================
   * TEXTES TRADUITS AVEC FALLBACK
   * ===================================================================
   *
   * Chaque texte utilise d’abord la clé de traduction si disponible.
   * Sinon, on garde un fallback français pour éviter toute régression.
   */
  const title = t.logoutModalTitle || "Votre sélection attend son heure";

  const bodyText =
    t.logoutModalText ||
    "Votre panier est préservé dans votre cave personnelle.\nEn quittant votre session, vous perdrez vos avantages membre et votre sélection sera réinitialisée.";

  const stayLabel = t.logoutModalStay || "Conserver mon panier";

  const leaveLabel =
    t.logoutModalLeave || "Continuer comme visiteur (réinitialiser le panier)";

  const moreItemsLabelTemplate =
    t.logoutModalMoreItems || "+ {count} autres délices...";

  /**
   * ===================================================================
   * FORMATAGE DU TEXTE MULTILIGNE
   * ===================================================================
   *
   * Le texte de la modal est stocké avec un retour à la ligne `\n`
   * dans translations.js.
   *
   * Ici, on le découpe pour l’afficher proprement ligne par ligne.
   */
  const bodyLines = String(bodyText).split("\n");

  /**
   * ===================================================================
   * FORMATAGE DU TEXTE "+ X AUTRES..."
   * ===================================================================
   */
  const moreItemsLabel = moreItemsLabelTemplate.replace(
    "{count}",
    Math.max(0, panier.length - 3)
  );

  return (
    <div className="logout-experience-modal">
      {/* ===============================================================
          STYLES INTERNES DU COMPOSANT
         ===============================================================
         
         Pourquoi ici ?
         - pour fournir un drop-in autonome
         - pour rapprocher le style des boutons du HeroSection
         - sans dépendre immédiatement d’un refactor CSS global
      */}
      <style>{`
        .logout-experience-modal {
          position: fixed;
          inset: 0;
          z-index: 10000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: rgba(0, 0, 0, 0.42);
        }

        .logout-experience-modal .modal-content {
          width: min(100%, 560px);
          background: #fff;
          border-radius: 22px;
          border: 1px solid rgba(0, 0, 0, 0.08);
          box-shadow:
            0 24px 60px rgba(0, 0, 0, 0.18),
            0 8px 18px rgba(0, 0, 0, 0.08);
          padding: 28px 24px 24px;
          text-align: center;
        }

        .logout-experience-modal .logout-modal-title {
          margin: 0;
          font-family: "Playfair Display", serif;
          font-size: clamp(1.35rem, 2vw, 1.7rem);
          line-height: 1.15;
          color: rgba(18, 18, 18, 0.96);
          letter-spacing: 0.02em;
        }

        .logout-experience-modal .logout-modal-text {
          font-family: var(--font-sans);
          color: #8c7e6d;
          font-size: 14px;
          line-height: 1.6;
          margin: 20px 0;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          font-weight: 500;
        }

        .logout-experience-modal .logout-modal-text-line + .logout-modal-text-line {
          margin-top: 4px;
        }

        .logout-experience-modal .mini-panier {
          margin: 30px 0;
          text-align: left;
          border-top: 1px solid #eee;
        }

        .logout-experience-modal .mini-panier-item {
          padding: 10px 0;
          border-bottom: 0.5px solid #eee;
          font-size: 13px;
          color: rgba(18, 18, 18, 0.9);
        }

        .logout-experience-modal .mini-panier-item small {
          color: #999;
        }

        .logout-experience-modal .mini-panier-more {
          font-size: 11px;
          color: #b3aca4;
          margin-top: 10px;
          text-align: center;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .logout-experience-modal .logout-modal-actions {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }

        /**
         * =============================================================
         * STYLE BOUTONS TYPE HEROSECTION / CARTE GASTRONOMIQUE
         * =============================================================
         *
         * On reprend ici l’esprit du HeroSection :
         * - fond clair travaillé
         * - coins arrondis
         * - léger relief
         * - ombre discrète
         * - rendu premium
         */
        .logout-experience-modal .logout-hero-btn {
          width: 100%;
          max-width: 360px;
          min-height: 46px;
          padding: 12px 18px;
          border-radius: 16px;
          border: 1px solid rgba(24, 24, 24, 0.14);
          background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.98) 0%,
            rgba(247, 244, 238, 0.96) 100%
          );
          color: rgba(18, 18, 18, 0.94);
          font-family: var(--font-sans);
          font-size: 0.78rem;
          line-height: 1.2;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          cursor: pointer;
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.92) inset,
            0 8px 20px rgba(0, 0, 0, 0.06),
            0 2px 6px rgba(0, 0, 0, 0.04);
          transition:
            transform 0.22s ease,
            box-shadow 0.22s ease,
            border-color 0.22s ease;
        }

        .logout-experience-modal .logout-hero-btn:hover {
          transform: translateY(-2px);
          border-color: rgba(168, 138, 76, 0.35);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.92) inset,
            0 12px 24px rgba(0, 0, 0, 0.08),
            0 3px 8px rgba(0, 0, 0, 0.05);
        }

        .logout-experience-modal .logout-hero-btn:active {
          transform: translateY(1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.88) inset,
            0 5px 12px rgba(0, 0, 0, 0.06);
        }

        /**
         * Variante secondaire : rester connecté
         */
        .logout-experience-modal .logout-hero-btn-stay {
          color: rgba(18, 18, 18, 0.94);
        }

        /**
         * Variante plus marquée : quitter / réinitialiser
         * On garde le même langage visuel,
         * avec une légère nuance plus chaude / décisionnelle.
         */
        .logout-experience-modal .logout-hero-btn-leave {
          border-color: rgba(176, 0, 32, 0.14);
          background: linear-gradient(
            180deg,
            rgba(255, 252, 252, 0.99) 0%,
            rgba(249, 241, 241, 0.96) 100%
          );
          color: #7d2432;
        }

        .logout-experience-modal .logout-hero-btn-leave:hover {
          border-color: rgba(176, 0, 32, 0.24);
        }

        @media (max-width: 640px) {
          .logout-experience-modal {
            padding: 14px;
          }

          .logout-experience-modal .modal-content {
            padding: 24px 18px 20px;
            border-radius: 18px;
          }

          .logout-experience-modal .logout-modal-title {
            font-size: 1.2rem;
          }

          .logout-experience-modal .logout-modal-text {
            font-size: 13px;
            letter-spacing: 1.2px;
          }

          .logout-experience-modal .logout-hero-btn {
            max-width: 100%;
            font-size: 0.74rem;
            letter-spacing: 0.14em;
          }
        }
      `}</style>

      <div className="modal-content page-transition">
        {/* =============================================================
            TITRE
           ============================================================= */}
        <h3 className="logout-modal-title">{title}</h3>

        {/* =============================================================
            TEXTE PRINCIPAL
           =============================================================
           
           On rend ici chaque ligne séparément afin de respecter le
           retour à la ligne contenu dans translations.js.
        */}
        <div className="logout-modal-text">
          {bodyLines.map((line, idx) => (
            <div key={idx} className="logout-modal-text-line">
              {line}
            </div>
          ))}
        </div>

        {/* =============================================================
            MINI APERÇU DU PANIER
           =============================================================
           
           On affiche :
           - jusqu’à 3 articles
           - puis une ligne résumé si le panier est plus long
        */}
        <div className="mini-panier">
          {panier.slice(0, 3).map((item, idx) => (
            <div key={idx} className="mini-panier-item">
              <span>
                {item.nom} <small>× {item.quantite || 1}</small>
              </span>
            </div>
          ))}

          {panier.length > 3 && (
            <p className="mini-panier-more">{moreItemsLabel}</p>
          )}
        </div>

        {/* =============================================================
            ACTIONS
           ============================================================= */}
        <div className="logout-modal-actions">
          <button
            className="logout-hero-btn logout-hero-btn-stay"
            onClick={() => setShow(false)}
          >
            {stayLabel}
          </button>

          <button
            className="logout-hero-btn logout-hero-btn-leave"
            onClick={handleConfirmLogout}
          >
            {leaveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
